import { Table, Typography, message } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import {
  EXPORT_ABSENTEEISM_INFORMED_REASON_REPORT,
  VIEW_ABSENTEEISM_INFORMED_REASON_REPORT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import {
  ABSENTEEISM_INFORMED_REASON_REPORT_GENERATE_PERMISSION,
  ABSINTHIUM_REPORT_GENERATE_PERMISSION,
} from "../../../routes/permissions";
import { hasPermission } from "../../../utils/functions";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";

const AbsenteeismInformedReasonReport = () => {
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : true,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterValues, setFilterValues] = useState({
    start_date: moment(),
    end_date: moment(),
  });
  const [metaData, setMetaData] = useState({
    current: 1,
    per_page: 10,
    total: 0,
  });

  const { Title } = Typography;
  const today = moment();

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const buildBody = useCallback(
    (values, page = 1, perPage = 10) => {
      const start_date = values?.start_date
        ? moment(values.start_date).format("YYYY-MM-DD")
        : today.format("YYYY-MM-DD");
      const end_date = values?.end_date
        ? moment(values.end_date).format("YYYY-MM-DD")
        : today.format("YYYY-MM-DD");

      return {
        start_date,
        end_date,
        page,
        per_page: perPage,
      };
    },
    [today],
  );

  const exportReport = async (values) => {
    const bodyData = buildBody(values || filterValues, 1, metaData.per_page);
    // Export should ignore pagination and send only date filters
    const exportBody = {
      start_date: bodyData.start_date,
      end_date: bodyData.end_date,
    };
    setLoading(true);

    const query = `${EXPORT_ABSENTEEISM_INFORMED_REASON_REPORT}`;
    const response = await getData(
      query,
      false,
      exportBody,
      { "Content-Type": "blob" },
      "arraybuffer",
    );

    if (response && response?.data) {
      const outputFilename = `absenteeism_informed_reason_${exportBody.start_date}_to_${exportBody.end_date}.xlsx`;
      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", outputFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setLoading(false);
      message.success(
        "Absenteeism Informed Reason report exported successfully",
      );
    } else {
      setLoading(false);
      message.error("Something went wrong while exporting");
    }
  };

  const fetchList = useCallback(
    async (values, page = 1, perPage = 10) => {
      setLoading(true);
      const bodyData = buildBody(values, page, perPage);
      const query = `${VIEW_ABSENTEEISM_INFORMED_REASON_REPORT}`;
      const response = await postData(query, bodyData);

      if (response?.code === 200) {
        const payload = response?.data || {};
        setTableData(payload?.data || []);
        setMetaData({
          current: payload?.current_page || page,
          per_page: payload?.per_page || perPage,
          total: payload?.total || 0,
        });
        setLoading(false);
      } else {
        setLoading(false);
        message.error(response?.message || "Something went wrong");
      }
    },
    [buildBody],
  );

  const previewFunction = async (values) => {
    const nextFilters = values || filterValues;
    setFilterValues(nextFilters);
    await fetchList(nextFilters, 1, metaData.per_page || 10);
  };

  useEffect(() => {
    fetchList(filterValues, metaData.current || 1, metaData.per_page || 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const onPageChange = (page, pageSize) => {
    fetchList(filterValues, page, pageSize || metaData.per_page || 10);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 120,
        fixed: "left",
      },
      {
        title: "id card no",
        dataIndex: "card_no",
        key: "card_no",
        width: 120,
        fixed: "left",
      },
      {
        title: "Operator name",
        dataIndex: "operator_name",
        key: "operator_name",
        width: 180,
        fixed: "left",
      },
      {
        title: (
          <span style={{ whiteSpace: "pre-line", lineHeight: 1.2 }}>
            {"Operator # Line\n(Absent)"}
          </span>
        ),
        dataIndex: "operator_line",
        key: "operator_line",
        width: 160,
        fixed: "left",
        align: "center",
      },
      {
        title: "Operator's Supervisor/LC",
        dataIndex: "supervisor",
        key: "supervisor",
        width: 180,
        fixed: "left",
      },
      {
        title: "Absence informed in advance (Y/N)",
        dataIndex: "is_informed_advance",
        key: "is_informed_advance",
        width: 220,
        align: "center",
      },
      {
        title: "reason",
        dataIndex: "reason",
        key: "reason",
        width: 180,
      },
      {
        title:
          "If not informed, supervisor attempted to contact worker (Y/N)",
        dataIndex: "is_superviser_contacted",
        key: "is_superviser_contacted",
        width: 280,
        align: "center",
      },
      {
        title: "Response from worker to supervisor (or non-response)",
        dataIndex: "worker_response",
        key: "worker_response",
        width: 280,
      },
    ],
    [],
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <ExtraContent
              refresh={refresh}
              setRefresh={setRefresh}
              hideAddButton={true}
              disabled={
                !hasPermission([
                  ABSENTEEISM_INFORMED_REASON_REPORT_GENERATE_PERMISSION,
                  ABSINTHIUM_REPORT_GENERATE_PERMISSION,
                ])
              }
              setShowFilter={setShowFilter}
              showFilter={showFilter}
            />
          }
          title={<Title level={5}>Absenteeism Informed Reason</Title>}
        />
      }
      content={
        <div className="table" style={{ marginTop: 10 }}>
          <FilterComponent
            tableData={tableData}
            filterAPI={exportReport}
            filterName={"absenteeism_informed_reason_report_filter"}
            searchButtonText={"Download"}
            defaultOpen
            setShowFilter={setShowFilter}
            showFilter={showFilter}
            isPreview={true}
            previewFunction={previewFunction}
            preview={"Preview"}
          />
          <Table
            loading={loading}
            dataSource={tableData}
            columns={tableColumns}
            pagination={{
              showSizeChanger: true,
              current: metaData.current,
              pageSize: metaData.per_page,
              total: metaData.total,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: onPageChange,
              onShowSizeChange: onPageChange,
            }}
            bordered
            sticky
            scroll={{ x: 1600 }}
            rowKey={(record, idx) =>
              `${record?.date || "d"}-${record?.card_no || "c"}-${idx}`
            }
          />
        </div>
      }
    />
  );
};

export default AbsenteeismInformedReasonReport;
